import type { RequiredGeneric } from '@pba/react-utils';
import type { Profile } from 'models';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Flex, Form, Row, Switch, Text } from 'ui';
import { NotificationCheckbox } from '../notification-checkbox';

export type NotificationsFormContentValues = {
  emailNotificationsSettings: {
    enableNotifications: boolean;
    events: boolean;
    giving: boolean;
    newsfeed: boolean;
    program: boolean;
  };
  pushNotificationsSettings: {
    enableNotifications: boolean;
    events: boolean;
    giving: boolean;
    messaging: boolean;
    newsfeed: boolean;
    program: boolean;
  };
};

type P = {
  firstInput: RefObject<HTMLButtonElement | null>;
  viewer: Profile;
};

const notificationsLabels = {
  events: 'Conferences and Events',
  giving: 'Giving',
  messaging: 'Messaging',
  newsfeed: 'Newsfeed',
  program: 'Program',
};

const NotificationsFormContent = <T extends NotificationsFormContentValues = never>({
  firstInput,
  viewer,
}: RequiredGeneric<T, P>) => {
  const { t } = useTranslation();

  return (
    <Row gutter={[0, 'xs']}>
      <Col span={8}>
        <Text size="small" type="title" uppercase weight="medium">
          Source
        </Text>
      </Col>
      <Col className="settings_notifications__center" span={8}>
        <Text size="small" type="title" uppercase weight="medium">
          Email
        </Text>
      </Col>
      <Col className="settings_notifications__center" span={8}>
        <Text size="small" type="title" uppercase weight="medium">
          Push
        </Text>
      </Col>

      <Col alignItems="center" span={8}>
        <Text size="small">All notifications</Text>
      </Col>
      <Col className="settings_notifications__center" span={8}>
        <Form.Item<NotificationsFormContentValues>
          dependencies={[['emailNotificationsSettings', 'enableNotifications', 'value']]}
          noStyle
        >
          {() => (
            <Flex justify="center">
              <Form.Item<NotificationsFormContentValues>
                name={['emailNotificationsSettings', 'enableNotifications']}
                valuePropName="checked"
              >
                <Switch aria-label={t('Enable/disable all email notifications')} ref={firstInput} size="small" />
              </Form.Item>
            </Flex>
          )}
        </Form.Item>
      </Col>
      <Col className="settings_notifications__center" span={8}>
        <Form.Item<NotificationsFormContentValues>
          dependencies={[['pushNotificationsSettings', 'enableNotifications', 'value']]}
          noStyle
        >
          {() => (
            <Flex justify="center">
              <Form.Item<NotificationsFormContentValues>
                name={['pushNotificationsSettings', 'enableNotifications']}
                valuePropName="checked"
              >
                <Switch aria-label={t('Enable/disable all push notifications')} size="small" />
              </Form.Item>
            </Flex>
          )}
        </Form.Item>
      </Col>

      <Col span={24}>
        <fieldset>
          <Row>
            <Col span={8}>
              <Text size="small">{notificationsLabels.newsfeed}</Text>
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="newsfeed" heading={notificationsLabels.newsfeed} type="email" user={viewer} />
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="newsfeed" heading={notificationsLabels.newsfeed} type="push" user={viewer} />
            </Col>
          </Row>
        </fieldset>
      </Col>

      <Col span={24}>
        <fieldset>
          <Row>
            <Col span={8}>
              <Text size="small">{notificationsLabels.events}</Text>
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="events" heading={notificationsLabels.events} type="email" user={viewer} />
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="events" heading={notificationsLabels.events} type="push" user={viewer} />
            </Col>
          </Row>
        </fieldset>
      </Col>

      <Col span={24}>
        <fieldset>
          <Row>
            <Col span={8}>
              <Text size="small">{notificationsLabels.messaging}</Text>
            </Col>
            <Col className="settings_notifications__center" offset={8} span={8}>
              <NotificationCheckbox
                area="messaging"
                heading={notificationsLabels.messaging}
                type="push"
                user={viewer}
              />
            </Col>
          </Row>
        </fieldset>
      </Col>

      <Col span={24}>
        <fieldset>
          <Row>
            <Col span={8}>
              <Text size="small">{notificationsLabels.program}</Text>
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="program" heading={notificationsLabels.program} type="email" user={viewer} />
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="program" heading={notificationsLabels.program} type="push" user={viewer} />
            </Col>
          </Row>
        </fieldset>
      </Col>

      <Col span={24}>
        <fieldset>
          <Row>
            <Col span={8}>
              <Text size="small">{notificationsLabels.giving}</Text>
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="giving" heading={notificationsLabels.giving} type="email" user={viewer} />
            </Col>
            <Col className="settings_notifications__center" span={8}>
              <NotificationCheckbox area="giving" heading={notificationsLabels.giving} type="push" user={viewer} />
            </Col>
          </Row>
        </fieldset>
      </Col>
    </Row>
  );
};

export default NotificationsFormContent;
